<template>
    <div class="footer-management">
      <!-- Layout Selection -->
      <div class="layout-section mb-6">
        <h3 class="section-title mb-4">LAYOUT</h3>
        <div class="layout-options">
          <v-radio-group v-model="selectedLayout" row @change="onLayoutChange">
            <v-radio
              label="2 Columns"
              value="2-columns"
              color="primary"
            ></v-radio>
            <v-radio
              label="3 Columns"
              value="3-columns"
              color="primary"
            ></v-radio>
          </v-radio-group>
        </div>
      </div>
  
      <!-- Columns Layout -->
      <div class="columns-layout">
        <v-row>
          <!-- Left Column -->
          <v-col :cols="selectedLayout === '2-columns' ? 6 : 4">
            <div class="column-header">
              <h4 class="column-title">Left Column</h4>
            </div>
            <draggable
              v-model="leftColumnItems"
              group="fields"
              class="drag-area"
              @change="onDragChange"
              tag="transition-group"
              name="drag-item"
            >
              <div
                v-for="item in leftColumnItems"
                :key="item.id"
                class="drag-item-card"
                :class="{ selected: selectedItem && selectedItem.id === item.id }"
                @click="selectItem(item)"
              >
                <div class="item-content">
                  <v-icon class="drag-handle mr-3">mdi-drag</v-icon>
                  <span class="item-number">{{ item.number }}</span>
                  <span class="item-name">{{ item.name }}</span>
                  <v-switch
                    v-model="item.enabled"
                    class="ml-auto"
                    dense
                    inset
                    color="primary"
                    @change="markLayoutAsChanged"
                    @click.stop
                  />
                </div>
              </div>
            </draggable>
          </v-col>
  
          <!-- Right Column -->
          <v-col :cols="selectedLayout === '2-columns' ? 6 : 4">
            <div class="column-header">
              <h4 class="column-title">Right Column</h4>
            </div>
            <draggable
              v-model="rightColumnItems"
              group="fields"
              class="drag-area"
              @change="onDragChange"
              tag="transition-group"
              name="drag-item"
            >
              <div
                v-for="item in rightColumnItems"
                :key="item.id"
                class="drag-item-card"
                :class="{ selected: selectedItem && selectedItem.id === item.id }"
                @click="selectItem(item)"
              >
                <div class="item-content">
                  <v-icon class="drag-handle mr-3">mdi-drag</v-icon>
                  <span class="item-number">{{ item.number }}</span>
                  <span class="item-name">{{ item.name }}</span>
                  <v-switch
                    v-model="item.enabled"
                    class="ml-auto"
                    dense
                    inset
                    color="primary"
                    @change="markLayoutAsChanged"
                    @click.stop
                  />
                </div>
              </div>
            </draggable>
          </v-col>
  
          <!-- Third Column (only for 3 columns layout) -->
          <v-col v-if="selectedLayout === '3-columns'" cols="4">
            <div class="column-header">
              <h4 class="column-title">Third Column</h4>
            </div>
            <draggable
              v-model="thirdColumnItems"
              group="fields"
              class="drag-area"
              @change="onDragChange"
              tag="transition-group"
              name="drag-item"
            >
              <div
                v-for="item in thirdColumnItems"
                :key="item.id"
                class="drag-item-card"
                :class="{ selected: selectedItem && selectedItem.id === item.id }"
                @click="selectItem(item)"
              >
                <div class="item-content">
                  <v-icon class="drag-handle mr-3">mdi-drag</v-icon>
                  <span class="item-number">{{ item.number }}</span>
                  <span class="item-name">{{ item.name }}</span>
                  <v-switch
                    v-model="item.enabled"
                    class="ml-auto"
                    dense
                    inset
                    color="primary"
                    @change="markLayoutAsChanged"
                    @click.stop
                  />
                </div>
              </div>
            </draggable>
          </v-col>
        </v-row>
      </div>
  
      <!-- Save Button -->
      <div class="text-center mt-6" v-if="hasChanges">
        <v-btn
          color="success"
          large
          @click="saveLayoutChanges"
          :loading="saving"
        >
          <v-icon left>mdi-content-save</v-icon>
          Save Layout Changes
        </v-btn>
      </div>
    </div>
  </template>
  
  <script>
  import draggable from "vuedraggable";
  
  export default {
    name: "Footer",
    components: {
      draggable,
    },
    props: {
      initialLayout: {
        type: String,
        default: "2-columns",
      },
      initialLeftItems: {
        type: Array,
        default: () => [],
      },
      initialRightItems: {
        type: Array,
        default: () => [],
      },
      initialThirdItems: {
        type: Array,
        default: () => [],
      },
      saving: {
        type: Boolean,
        default: false,
      },
    },
    data() {
      return {
        selectedLayout: this.initialLayout,
        selectedItem: null,
        hasChanges: false,
  
        leftColumnItems: [...this.initialLeftItems],
        rightColumnItems: [...this.initialRightItems],
        thirdColumnItems: [...this.initialThirdItems],
      };
    },
  
    watch: {
      initialLayout(newVal) {
        this.selectedLayout = newVal;
      },
      initialLeftItems(newVal) {
        this.leftColumnItems = [...newVal];
      },
      initialRightItems(newVal) {
        this.rightColumnItems = [...newVal];
      },
      initialThirdItems(newVal) {
        this.thirdColumnItems = [...newVal];
      },
      selectedLayout() {
        if (
          this.selectedLayout === "2-columns" &&
          this.thirdColumnItems.length > 0
        ) {
          this.rightColumnItems.push(...this.thirdColumnItems);
          this.thirdColumnItems = [];
          this.updateItemNumbers();
          this.markLayoutAsChanged();
        }
      },
    },
  
    methods: {
      selectItem(item) {
        this.selectedItem = item;
        this.$emit("item-selected", item);
      },
  
      onLayoutChange() {
        this.markLayoutAsChanged();
        this.$emit("layout-changed", this.selectedLayout);
      },
  
      onDragChange() {
        this.updateItemNumbers();
        this.markLayoutAsChanged();
      },
  
      updateItemNumbers() {
        let counter = 1;
  
        this.leftColumnItems.forEach((item) => {
          item.number = counter.toString();
          counter++;
        });
  
        this.rightColumnItems.forEach((item) => {
          item.number = counter.toString();
          counter++;
        });
  
        if (this.selectedLayout === "3-columns") {
          this.thirdColumnItems.forEach((item) => {
            item.number = counter.toString();
            counter++;
          });
        }
      },
  
      markLayoutAsChanged() {
        this.hasChanges = true;
        this.$emit("changes-made");
      },
  
      saveLayoutChanges() {
        const layoutData = {
          layout: this.selectedLayout,
          left_column: this.leftColumnItems,
          right_column: this.rightColumnItems,
          third_column: this.thirdColumnItems,
        };
  
        this.$emit("save-layout", layoutData);
      },
  
      getLayoutData() {
        return {
          layout: this.selectedLayout,
          left_column: this.leftColumnItems,
          right_column: this.rightColumnItems,
          third_column: this.thirdColumnItems,
        };
      },
    },
  
    mounted() {
      this.updateItemNumbers();
    },
  };
  </script>
  
  <style lang="scss" scoped>
  .footer-management {
    padding: 20px;
  
    .section-title {
      font-size: 14px;
      font-weight: 600;
      color: #666;
      letter-spacing: 0.5px;
    }
  
    .layout-options {
      padding: 16px 0;
    }
  
    .columns-layout {
      .column-header {
        padding: 16px 0;
        border-bottom: 1px solid #e0e0e0;
        margin-bottom: 16px;
  
        .column-title {
          font-size: 16px;
          font-weight: 500;
          color: #424242;
        }
      }
  
      .drag-area {
        min-height: 200px;
        padding: 8px;
        border-radius: 8px;
        background-color: #fafafa;
        border: 2px dashed #e0e0e0;
        transition: all 0.2s ease;
  
        &:hover {
          border-color: #1976d2;
          background-color: #f8f9ff;
        }
      }
  
      .drag-item-card {
        background: white;
        border-radius: 8px;
        margin-bottom: 8px;
        border: 1px solid #e0e0e0;
        cursor: pointer;
        transition: all 0.2s ease;
  
        &:hover {
          border-color: #1976d2;
          box-shadow: 0 2px 8px rgba(25, 118, 210, 0.1);
        }
  
        &.selected {
          border-color: #1976d2;
          box-shadow: 0 2px 12px rgba(25, 118, 210, 0.2);
          background-color: #f8f9ff;
        }
  
        .item-content {
          display: flex;
          align-items: center;
          padding: 12px 16px;
  
          .drag-handle {
            color: #9e9e9e;
            cursor: grab;
  
            &:active {
              cursor: grabbing;
            }
          }
  
          .item-number {
            background: #1976d2;
            color: white;
            border-radius: 50%;
            width: 24px;
            height: 24px;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 12px;
            font-weight: 600;
            margin-right: 12px;
          }
  
          .item-name {
            font-weight: 500;
            color: #424242;
          }
        }
      }
    }
  }
  
  /* Drag transition animations */
  .drag-item-enter-active,
  .drag-item-leave-active {
    transition: all 0.2s ease;
  }
  
  .drag-item-enter,
  .drag-item-leave-to {
    opacity: 0;
    transform: translateX(30px);
  }
  
  .drag-item-move {
    transition: transform 0.2s ease;
  }
  </style>
  